import { inject } from '@angular/core';
import {
  BaseWebsocketMessage,
  SubWebsocketMessageRequest,
  WebsocketEntTypes,
  WebSocketService,
  WSEntityManager,
} from '@qtek/core/websockets-core';
import {
  BackendResponse,
  DocumentAttribute,
  DocumentReferenceBase,
  DocumentReferenceEntityType,
} from '@qtek/shared/models';
import { Observable } from 'rxjs';
import { HttpEntityManager } from '../../../http-entity-manager';

/**
 * Generic core for the 6 `*DocumentReference` tables (Company/Person/Item/
 * Chat/TemplateData/NdaTemplate). Concrete services only declare which table
 * they talk to (`entityName`), which `entityType` value identifies them on
 * upload (`entityType`), and their own response shape (`T`, one of the
 * entity-specific interfaces extending `DocumentReferenceBase` in
 * `document-reference.model.ts`) — everything else (list/get/put/delete/WS
 * subscribe) is shared here so the 6 entities never duplicate CRUD logic.
 * Single-reference GET/PUT/POST (`get(id)`/`put(payload, id)`/`post(payload)`,
 * used by the store's `fetchById`/`update`/`create`) override
 * `HttpEntityManager` just to fold in `auditNames=true`, same as `list`
 * below — `delete` is left inherited as-is since its response carries no
 * audit fields to resolve.
 *
 * `entityType` is never inferred from `attribute` — some attributes
 * (CREDIT_AGREEMENT, DEAL_PROCESSOR, CONTEXT_*, ...) aren't uniquely owned by
 * one entity, so callers always pass entityType explicitly via the concrete
 * per-entity service/store they inject.
 */
export abstract class DocumentReferenceService<
  T extends DocumentReferenceBase = DocumentReferenceBase,
> extends HttpEntityManager<T, T> {
  private wsService = inject(WebSocketService);

  abstract readonly entityType: DocumentReferenceEntityType;

  private get wsManager(): WSEntityManager {
    return new WSEntityManager(
      this.wsService,
      this.entityName as WebsocketEntTypes
    );
  }

  /**
   * `auditNames=true` is always sent so `_cby`/`_uby` come back with the
   * actor's display name, not just their id — shared by all 6 entities
   * rather than overridden per-entity.
   */
  list(
    attribute?: DocumentAttribute | readonly DocumentAttribute[],
    entityId?: string
  ): Observable<BackendResponse<T[]>> {
    const attributes =
      attribute == null
        ? []
        : Array.isArray(attribute)
          ? attribute
          : [attribute];

    return this.http.get<BackendResponse<T[]>>(this.getUrl(), {
      ...this.baseRequestOptions,
      params: this.searchParams({
        ...(attributes.length ? { attribute: attributes.join(',') } : {}),
        ...(entityId ? { entityId } : {}),
        auditNames: 'true',
      }),
    });
  }

  /** Same `auditNames=true` as `list` — used by the store's `fetchById`. */
  override get(
    id?: string | number,
    query?: any
  ): Observable<BackendResponse<T>> {
    return super.get(id, { ...query, auditNames: 'true' });
  }

  /** Same `auditNames=true` as `list` — used by the store's `update`. */
  override put(
    payload: any,
    id?: string | number,
    query?: any
  ): Observable<BackendResponse<T>> {
    return super.put(payload, id, { ...query, auditNames: 'true' });
  }

  /** Same `auditNames=true` as `list` — used by the store's `create`. */
  override post(
    payload: any,
    id?: string | number,
    query?: any
  ): Observable<BackendResponse<T>> {
    return super.post(payload, id, { ...query, auditNames: 'true' });
  }

  subscribe(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>,
    withQuery = false
  ): Observable<BaseWebsocketMessage> {
    return this.wsManager.subscribeEntity(payload, withQuery);
  }
}
